import { useStripeConfirm } from 'src/app/shared/components/payments';
import type { WithTokenParams } from 'src/app/shared/types';
import { paymentMethod } from 'src/app/shared/utils/payments';

import { usePromoteFormState } from '../../../formContext/formState';
import { useCreatePromotion } from '../useCreatePromotion';
import { useNavigateToMembershipThankYouPage } from '../useNavigateToMembershipThankYouPage';
import { useNavigateToThankYouPage } from '../useNavigateToThankYouPage';
import { usePromotionChargePaymentMethod } from '../usePaymentMethodFunctions';
import { usePPMU } from '../usePPMU';
import { useTracking } from '../useTracking';
import { useTrackModelSuccess } from '../useTrackModelSuccess';

import { useHandleError } from './hooks/useHandleError';

export function useSubmit(): (params: WithTokenParams) => Promise<void> {
	const [, { setLoading }] = usePromoteFormState();

	const { trackSubmitValid } = useTracking();

	const createPromotion = useCreatePromotion();
	const navigateToThankYouPage = useNavigateToThankYouPage();
	const navigateToMembershipThankYouPage = useNavigateToMembershipThankYouPage();
	const confirmStripe = useStripeConfirm();
	// useButtonBanditReward and useComboBanditReward skipped - bandits not available in formData

	const processPPMU = usePPMU();

	const promotionChargePaymentMethod = usePromotionChargePaymentMethod();
	const handleError = useHandleError();

	const trackModelSuccess = useTrackModelSuccess();

	return async function submit({ paymentType, token, cardType, onTransactionComplete }) {
		trackSubmitValid({ paymentType });
		setLoading();
		try {
			const result = await createPromotion(promotionChargePaymentMethod(paymentType, token), paymentType);
			await confirmStripe(result, paymentMethod(paymentType, token));
			onTransactionComplete && onTransactionComplete('success');
			trackModelSuccess({
				paymentType,
				cardType,
			});
			// rewardButtonBandit and rewardComboBandit skipped - bandits not available
			const ppmuResult = await processPPMU({
				token: result?.paymentMethodId || token,
				accountId: result?.accountId,
				paymentType,
			});
			if (ppmuResult?.accepted) {
				navigateToMembershipThankYouPage(ppmuResult.amount);
			} else {
				navigateToThankYouPage();
			}
		} catch (error: unknown) {
			handleError(error, paymentType, onTransactionComplete);
		}
	};
}
